import { Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';

import { ERROR_PAGE_PATH, allowSystemPageNavigation } from '../../../guard/navigation-flag.guard';
import { ToastInfo } from '../case-service/case.service';

const NET_ERR_PATTERN = /net::err_[a-z0-9_]+/i;

const NETWORK_ERROR_MESSAGES = [
  'failed to fetch',
  'networkerror when attempting to fetch resource',
  'network request failed',
  'load failed',
  'err_internet_disconnected',
  'err_connection_refused',
  'err_connection_timed_out',
  'err_connection_reset',
  'err_connection_closed',
  'err_name_not_resolved',
  'err_address_unreachable',
  'err_tunnel_connection_failed',
  'err_proxy_connection_failed',
  'err_ssl_protocol_error',
  'err_cert_authority_invalid',
  'the internet connection appears to be offline',
];

const NETWORK_ERROR_NAMES = new Set(['NetworkError', 'TimeoutError']);

const ABORT_ERR_CODE = 20; // DOMException.ABORT_ERR

const NETWORK_ERROR_DETAILS: Record<string, string> = {
  ERR_NAME_NOT_RESOLVED: 'The app could not find the API server address. Check the server address or your network connection.',
  ERR_CONNECTION_REFUSED: 'The API server refused the connection. It may be stopped or unavailable.',
  ERR_CONNECTION_TIMED_OUT: 'The connection to the API server timed out. Check your network and try again.',
  ERR_TIMED_OUT: 'The connection to the API server timed out. Check your network and try again.',
  ERR_CONNECTION_RESET: 'The connection to the API server was interrupted. Try again.',
  ERR_CONNECTION_CLOSED: 'The connection to the API server was closed unexpectedly. Try again.',
  ERR_INTERNET_DISCONNECTED: 'Your device appears to be offline. Restore your network connection and try again.',
  ERR_ADDRESS_UNREACHABLE: 'The API server address cannot be reached from this network.',
  ERR_TUNNEL_CONNECTION_FAILED: 'The app could not connect through the configured network proxy.',
  ERR_PROXY_CONNECTION_FAILED: 'The app could not connect through the configured network proxy.',
  ERR_SSL_PROTOCOL_ERROR: 'A secure connection to the API server could not be established.',
  ERR_CERT_AUTHORITY_INVALID: 'The API server certificate is not trusted by this browser.',
};

@Injectable({ providedIn: 'root' })
export class ConnectionErrorService {
  private readonly router = inject(Router);
  readonly errorToast = signal<ToastInfo | null>(null);
  private navigating = false;

  isConnectionFailure(error: unknown): boolean {
    if (error === null || error === undefined) {
      return false;
    }

    const text = this.describe(error);

    if (this.isAbort(error, text)) {
      return false;
    }

    if (NET_ERR_PATTERN.test(text)) {
      return true;
    }

    if (NETWORK_ERROR_MESSAGES.some((message) => text.includes(message))) {
      return true;
    }

    if (NETWORK_ERROR_NAMES.has(this.nameOf(error))) {
      return true;
    }

    const status = this.statusOf(error);
    return status === 0 || status === 502 || status === 503 || status === 504;
  }

  /**
   * Sends the user to the error boundary page. Safe to call from every failing
   * request at once: repeated calls while a navigation is in flight, or calls
   * made while already on the page, are no-ops.
   */
  navigateToErrorPage(reason?: unknown): void {
    if (this.navigating || this.isOnErrorPage()) {
      return;
    }

    const { message, correlationId } = this.getUserMessage(reason);
    this.errorToast.set({
      message,
      type: 'error',
      id: Date.now(),
      persistent: true,
      ...(correlationId ? { correlationId } : {}),
    });

    if (reason !== undefined) {
      console.error('[Connection Error] Routing to error boundary', reason);
    }

    this.navigating = true;
    allowSystemPageNavigation(ERROR_PAGE_PATH);

    this.router.navigateByUrl(ERROR_PAGE_PATH).then(
      (navigated) => this.settle(Boolean(navigated)),
      () => this.settle(false),
    );
  }

  registerGlobalListeners(): () => void {
    const onError = (event: ErrorEvent) => {
      const cause = event.error ?? event.message;
      if (this.isConnectionFailure(cause)) {
        event.preventDefault();
        this.navigateToErrorPage(cause);
      }
    };

    const onRejection = (event: PromiseRejectionEvent) => {
      if (this.isConnectionFailure(event.reason)) {
        event.preventDefault();
        this.navigateToErrorPage(event.reason);
      }
    };

    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);

    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }

  private settle(navigated: boolean): void {
    this.navigating = false;

    if (navigated || this.isOnErrorPage()) {
      return;
    }

    // The router refused the navigation (e.g. a guard bounced it). Reload
    // directly so the user is never stranded on a stale page.
    allowSystemPageNavigation(ERROR_PAGE_PATH);
    window.location.assign(ERROR_PAGE_PATH);
  }

  private isOnErrorPage(): boolean {
    return (this.router.url || '').split('?')[0].split('#')[0] === ERROR_PAGE_PATH;
  }

  private isAbort(error: unknown, text: string): boolean {
    const candidate = error as { name?: unknown; code?: unknown; error?: { type?: unknown } };

    if (candidate?.name === 'AbortError' || candidate?.code === ABORT_ERR_CODE) {
      return true;
    }

    // HttpClient wraps the cancellation in a ProgressEvent of type 'abort'.
    if (candidate?.error?.type === 'abort') {
      return true;
    }

    return /\baborted\b/.test(text) && !NET_ERR_PATTERN.test(text);
  }

  private statusOf(error: unknown): number | undefined {
    return (error as { status?: unknown })?.status as number | undefined;
  }

  private nameOf(error: unknown): string {
    return String((error as { name?: unknown })?.name ?? '');
  }

  private getUserMessage(reason: unknown): { message: string; correlationId?: string } {
    const error = this.asRecord(reason);
    const nestedError = this.asRecord(error?.['error']);
    const status = typeof error?.['status'] === 'number' ? error['status'] : undefined;
    const correlationId =
      this.asString(nestedError?.['correlationId']) ??
      this.asString(error?.['correlationId']);

    const responseMessage = this.getResponseMessage(error, nestedError);
    if (responseMessage) {
      return { message: responseMessage, ...(correlationId ? { correlationId } : {}) };
    }

    const describedError = this.describe(reason);
    const networkErrorCode = describedError.match(/(?:net::)?(ERR_[A-Z0-9_]+)/i)?.[1]?.toUpperCase();
    let message = networkErrorCode
      ? NETWORK_ERROR_DETAILS[networkErrorCode] ??
        `The app could not connect to the API server (${networkErrorCode}). Check your network connection and try again.`
      : '';

    if (!message) {
      const errorMessage = this.asString(error?.['message'])?.trim();
      if (errorMessage && error?.['name'] !== 'HttpErrorResponse') {
        message = errorMessage;
      } else {
        switch (status) {
          case 0:
            message = 'The app could not connect to the API server. Check your network connection and try again.';
            break;
          case 502:
            message = 'The API gateway could not reach the API server (502 Bad Gateway). The server may be unavailable; try again shortly.';
            break;
          case 503:
            message = 'The API server is temporarily unavailable (503 Service Unavailable). Try again shortly.';
            break;
          case 504:
            message = 'The API server did not respond in time (504 Gateway Timeout). Try again shortly.';
            break;
          default:
            if (status !== undefined && status >= 500) {
              message = `The API server encountered an error (HTTP ${status}). Try again shortly.`;
            } else {
              message = 'An unexpected error occurred in the app. Refresh the page, and try again.';
            }
        }
      }
    }

    return { message, ...(correlationId ? { correlationId } : {}) };
  }

  private getResponseMessage(
    error: Record<string, unknown> | undefined,
    responseBody: Record<string, unknown> | undefined,
  ): string | undefined {
    if (responseBody) {
      for (const key of ['errorMessage', 'message', 'detail', 'description', 'title']) {
        const message = this.asString(responseBody[key])?.trim();
        if (message) {
          return message;
        }
      }
    }

    const topLevelMessage = this.asString(error?.['errorMessage'])?.trim();
    if (topLevelMessage) {
      return topLevelMessage;
    }

    const rawBody = this.asString(error?.['error'])?.trim();
    return rawBody && !/<\/?[a-z][\s\S]*>/i.test(rawBody) ? rawBody : undefined;
  }

  private asRecord(value: unknown): Record<string, unknown> | undefined {
    return typeof value === 'object' && value !== null
      ? (value as Record<string, unknown>)
      : undefined;
  }

  private asString(value: unknown): string | undefined {
    return typeof value === 'string' && value.length > 0 ? value : undefined;
  }

  clearErrorToast(): void {
    this.errorToast.set(null);
  }

  private describe(error: unknown): string {
    if (typeof error === 'string') {
      return error.toLowerCase();
    }

    const candidate = error as {
      name?: unknown;
      message?: unknown;
      statusText?: unknown;
      error?: unknown;
    };

    const nested =
      typeof candidate?.error === 'object' && candidate.error !== null
        ? (candidate.error as { message?: unknown; name?: unknown })
        : null;

    const parts: unknown[] = [
      candidate?.name,
      candidate?.message,
      candidate?.statusText,
      typeof candidate?.error === 'string' ? candidate.error : null,
      nested?.message,
      nested?.name,
    ];

    return parts
      .filter((part): part is string => typeof part === 'string')
      .join(' | ')
      .toLowerCase();
  }
}
